import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery } from 'urql'
import { graphql } from 'app/generated/gql'
import type { CreateStoredCredentialInput, UpdateStoredCredentialInput } from 'app/generated/gql/graphql'
import { useConfirmation } from 'app/components/ConfirmationContext'
import { useAuthStore } from 'app/stores/StoreContext'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { opsClient } from 'app/lib/opsUrqlClient'
import type { LocationJson, WorkspaceJson } from 'app/types/entities'
import { logError } from 'lib/logging'

const STORED_CREDENTIALS_QUERY = graphql(`
  query StoredCredentials($search: String) {
    storedCredentials(search: $search) {
      hits {
        id
        orgId
        workspaceId
        locationId
        label
        username
        metadata
        createdBy
        updatedBy
        createdDate
        updatedDate
      }
      total
      page
    }
  }
`)

const STORED_CREDENTIAL_PASSWORD_QUERY = graphql(`
  query StoredCredentialPassword($id: ID!) {
    storedCredentialPassword(id: $id) {
      id
      password
    }
  }
`)

const CREATE_STORED_CREDENTIAL_MUTATION = graphql(`
  mutation CreateStoredCredential($targetWorkspaceId: ID!, $input: CreateStoredCredentialInput!) {
    createStoredCredential(targetWorkspaceId: $targetWorkspaceId, input: $input) {
      id
      orgId
      workspaceId
      locationId
      label
      username
      metadata
      createdBy
      updatedBy
      createdDate
      updatedDate
    }
  }
`)

const UPDATE_STORED_CREDENTIAL_MUTATION = graphql(`
  mutation UpdateStoredCredential($id: ID!, $input: UpdateStoredCredentialInput!) {
    updateStoredCredential(id: $id, input: $input) {
      id
      orgId
      workspaceId
      locationId
      label
      username
      metadata
      createdBy
      updatedBy
      createdDate
      updatedDate
    }
  }
`)

const DELETE_STORED_CREDENTIAL_MUTATION = graphql(`
  mutation DeleteStoredCredential($id: ID!) {
    deleteStoredCredential(id: $id)
  }
`)

// Super-user cross-workspace location lookup lives on the ops schema. hasConnector
// is set to false so ALL locations count, not only connector-linked ones.
const OPS_LOCATIONS_QUERY = graphql(`
  query StoredCredentialOpsLocations($filter: LocationFilter) {
    opsLocations(filter: $filter) {
      nodes {
        id
        name
        workspaceId
      }
    }
  }
`)

export type StoredCredentialFormData = {
  label: string
  username: string
  password: string
  metadata?: string
}

export type StoredCredentialRow = {
  id: string
  orgId: string
  workspaceId: string
  locationId?: string | null
  label: string
  username: string
  metadata?: string | null
  createdBy: string
  updatedBy: string
  createdDate: string
  updatedDate: string
}

export type UseStoredCredentialsReturn = {
  workspace: WorkspaceJson | undefined
  location: LocationJson | undefined
  /**
   * True when the location selector should be disabled: the workspace has zero
   * locations, or exactly one (which is auto-selected). Multiple locations keep
   * it enabled so a scope can be chosen.
   */
  locationSelectorDisabled: boolean
  locationMap: Record<string, string>
  onWorkspaceChange: (workspace: WorkspaceJson) => void
  onLocationChange: (location: LocationJson) => void
  onClearLocation: () => void
  credentials: StoredCredentialRow[]
  loading: boolean
  error: string | undefined
  isEditing: boolean
  editingId: string | undefined
  startCreate: () => void
  startEdit: (id: string) => void
  cancelEdit: () => void
  submitHandler: (data: StoredCredentialFormData) => Promise<void>
  deleteHandler: (id: string, label: string) => void
  revealedPasswords: Record<string, string>
  revealingId: string | undefined
  reveal: (id: string) => Promise<void>
  hideRevealed: (id: string) => void
}

export function useStoredCredentials(): UseStoredCredentialsReturn {
  const [confirm] = useConfirmation()
  const auth = useAuthStore()
  // auth.workspace resolves asynchronously (fetchMe loads it after the user),
  // so seed it in an effect once available. `seededDefault` guards against
  // re-seeding after the user deliberately switches.
  const [workspace, setWorkspace] = useState<WorkspaceJson | undefined>(() =>
    auth.workspace?.id ? ({ id: auth.workspace.id, name: auth.workspace.name } as WorkspaceJson) : undefined,
  )
  const [seededDefault, setSeededDefault] = useState<boolean>(() => !!auth.workspace?.id)
  const [location, setLocation] = useState<LocationJson>()
  // Number of locations in the selected workspace (undefined until resolved).
  // Drives whether the location selector is disabled (0 or 1 location).
  const [locationCount, setLocationCount] = useState<number | undefined>()
  const [locationMap, setLocationMap] = useState<Record<string, string>>({})
  const [editingId, setEditingId] = useState<string | undefined>()
  const [isEditing, setIsEditing] = useState(false)
  const [revealedPasswords, setRevealedPasswords] = useState<Record<string, string>>({})
  const [revealingId, setRevealingId] = useState<string | undefined>()

  // Seed the default workspace once auth resolves it. The component observes the
  // auth store, so this effect re-runs when auth.workspace populates after the
  // initial render. Only seeds when the user has not yet chosen a workspace.
  const authWorkspaceId = auth.workspace?.id
  const authWorkspaceName = auth.workspace?.name
  useEffect(() => {
    if (seededDefault || workspace || !authWorkspaceId) return
    setWorkspace({ id: authWorkspaceId, name: authWorkspaceName } as WorkspaceJson)
    setSeededDefault(true)
  }, [seededDefault, workspace, authWorkspaceId, authWorkspaceName])

  const workspaceId = workspace?.id
  const locationId = location?.id

  // This subtree is wrapped in a urql Provider bound to the ops client (stored
  // credentials are ops-schema only), so useQuery/useMutation here target
  // /graphql/ops natively — no per-call context override needed.
  const searchTerms = [`workspaceId:${workspaceId}`]
  if (locationId) searchTerms.push(`locationId:${locationId}`)
  const [queryResult, refetch] = useQuery({
    query: STORED_CREDENTIALS_QUERY,
    variables: { search: searchTerms.join(` `) },
    pause: !workspaceId,
  })

  const [, executeCreate] = useMutation(CREATE_STORED_CREDENTIAL_MUTATION)
  const [, executeUpdate] = useMutation(UPDATE_STORED_CREDENTIAL_MUTATION)
  const [, executeDelete] = useMutation(DELETE_STORED_CREDENTIAL_MUTATION)

  const credentials = useMemo(
    () => (queryResult.data?.storedCredentials.hits ?? []) as StoredCredentialRow[],
    [queryResult.data],
  )

  const onWorkspaceChange = (ws: WorkspaceJson) => {
    setWorkspace(ws)
    setLocation(undefined)
    setLocationCount(undefined)
    setRevealedPasswords({})
    cancelEdit()
  }

  // Resolve the selected workspace's locations. When there is exactly one, select
  // it automatically; the location selector stays visible but disabled (there is
  // nothing else to pick). Zero locations also disables it. Two or more keep it
  // enabled so a scope can be chosen.
  useEffect(() => {
    if (!workspaceId) {
      setLocationCount(undefined)
      return
    }
    let cancelled = false
    // Query the ops schema so a super user can resolve locations for ANY workspace
    // in their org (the public `locations` query force-scopes to the caller's own
    // workspace). hasConnector:false counts every location, not just linked ones.
    opsClient
      .query(
        OPS_LOCATIONS_QUERY,
        { filter: { search: `workspaceId.keyword:${workspaceId}`, hasConnector: false } },
        { requestPolicy: `network-only` },
      )
      .toPromise()
      .then((result) => {
        if (cancelled) return
        if (result.error) {
          logError(`Failed to load workspace locations`, result.error)
          setLocationCount(undefined)
          return
        }
        const nodes = (result.data?.opsLocations?.nodes ?? []) as Array<{ id: string; name: string }>
        setLocationCount(nodes.length)
        setLocationMap(Object.fromEntries(nodes.map((n) => [n.id, n.name])))
        if (nodes.length >= 1) {
          // Auto-select the first location so the page opens with a scope.
          setLocation({ id: nodes[0].id, name: nodes[0].name } as LocationJson)
        }
      })
      .catch((err) => {
        if (cancelled) return
        logError(`Failed to load workspace locations`, err)
        setLocationCount(undefined)
      })
    return () => {
      cancelled = true
    }
  }, [workspaceId])

  const onLocationChange = (loc: LocationJson) => {
    setLocation(loc)
  }

  const onClearLocation = () => {
    setLocation(undefined)
  }

  const startCreate = () => {
    setEditingId(undefined)
    setIsEditing(true)
  }

  const startEdit = (id: string) => {
    setEditingId(id)
    setIsEditing(true)
  }

  const cancelEdit = () => {
    setEditingId(undefined)
    setIsEditing(false)
  }

  const submitHandler = async (data: StoredCredentialFormData) => {
    if (!workspaceId) return

    if (editingId) {
      const input: UpdateStoredCredentialInput = {
        label: data.label,
        username: data.username,
        metadata: data.metadata,
      }
      if (data.password) input.password = data.password

      const result = await executeUpdate({ id: editingId, input })
      if (result.error) {
        logError(`Failed to update stored credential`, result.error)
        notifyError(`Failed to update credential`)
        return
      }
      notifySuccess(`Credential updated`)
    } else {
      const input: CreateStoredCredentialInput = {
        locationId,
        label: data.label,
        username: data.username,
        password: data.password,
        metadata: data.metadata,
      }

      const result = await executeCreate({ targetWorkspaceId: workspaceId, input })
      if (result.error) {
        logError(`Failed to create stored credential`, result.error)
        notifyError(`Failed to create credential`)
        return
      }
      notifySuccess(`Credential created`)
    }

    cancelEdit()
    refetch({ requestPolicy: `network-only` })
  }

  const deleteHandler = (id: string, label: string) => {
    confirm({
      title: `Delete Credential`,
      description: `Are you sure you want to delete "${label}"? This cannot be undone.`,
      variant: `danger`,
    })
      .then(async () => {
        const result = await executeDelete({ id })
        if (result.error) {
          logError(`Failed to delete stored credential`, result.error)
          notifyError(`Failed to delete credential`)
          return
        }
        notifySuccess(`Credential deleted`)
        setRevealedPasswords((prev) => {
          const next = { ...prev }
          delete next[id]
          return next
        })
        refetch({ requestPolicy: `network-only` })
      })
      .catch(() => {})
  }

  // Imperative one-shot query (not a mounted useQuery) so the audit event
  // never fires implicitly during the list render.
  const reveal = async (id: string) => {
    setRevealingId(id)
    const result = await opsClient
      .query(STORED_CREDENTIAL_PASSWORD_QUERY, { id }, { requestPolicy: `network-only` })
      .toPromise()
    const password = result.data?.storedCredentialPassword?.password
    if (result.error || !password) {
      logError(`Failed to reveal stored credential password`, result.error)
      notifyError(`Failed to reveal password`)
      setRevealingId(undefined)
      return
    }
    setRevealedPasswords((prev) => ({ ...prev, [id]: password }))
    setRevealingId(undefined)
  }

  const hideRevealed = (id: string) => {
    setRevealedPasswords((prev) => {
      const next = { ...prev }
      delete next[id]
      return next
    })
  }

  return {
    workspace,
    location,
    locationSelectorDisabled: locationCount === undefined || locationCount <= 1,
    locationMap,
    onWorkspaceChange,
    onLocationChange,
    onClearLocation,
    credentials,
    loading: queryResult.fetching,
    error: queryResult.error?.message,
    isEditing,
    editingId,
    startCreate,
    startEdit,
    cancelEdit,
    submitHandler,
    deleteHandler,
    revealedPasswords,
    revealingId,
    reveal,
    hideRevealed,
  }
}
